import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { TraceSpan } from '../interfaces/trace-span';

export type { TraceSpan } from '../interfaces/trace-span';

/**
 * Generates and propagates W3C Trace Context headers so that
 * frontend and backend spans can be stitched together in Grafana Tempo.
 *
 * ┌──────────┐   traceparent   ┌──────────┐   traceparent   ┌──────────┐
 * │  Browser  │ ──────────────→ │   API     │ ──────────────→ │   DB     │
 * │  (root)   │  00-tid-sid-01 │  (child)  │  00-tid-sid-01 │ (child)  │
 * └──────────┘                 └──────────┘                 └──────────┘
 *
 * All three share the same traceId → one trace in Grafana.
 *
 * Usage:
 *   const span = traceService.startSpan('GET /api/scholarships', 'client');
 *   // ... work ...
 *   span.finish();
 */
@Injectable({ providedIn: 'root' })
export class TraceService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly isBrowser = isPlatformBrowser(this.platformId);

  /** Currently-active span (simplified – real OTel has a stack) */
  private activeSpan: TraceSpan | null = null;

  /** Whether tracing is enabled. */
  private enabled = true;

  /**
   * W3C trace flags:
   *   01 = sampled (included in trace)
   *   00 = not sampled
   */
  private readonly TRACE_FLAGS_SAMPLED = '01';

  // ---- Public API --------------------------------------------------------

  /** Enable / disable tracing at runtime */
  setEnabled(v: boolean): void {
    this.enabled = v;
  }

  /**
   * Start a new span. Returns the span and sets it as the current active span.
   *
   * If there's already an active span, the new span becomes a child of it.
   * If there's no active span, a new trace is created.
   */
  startSpan(name: string, kind: TraceSpan['kind'] = 'client'): TraceSpan {
    if (!this.enabled || !this.isBrowser) {
      return this.noopSpan();
    }

    const parentSpanId = this.activeSpan?.spanId ?? '';
    const traceId = this.activeSpan?.traceId ?? this.generateTraceId();

    const span: TraceSpan = {
      traceId,
      spanId: this.generateSpanId(),
      parentSpanId,
      name,
      kind,
      startTime: Date.now(),
      status: 'unset',
      attributes: {},
      events: [],
    };

    this.activeSpan = span;
    return span;
  }

  /**
   * Finish the given span (or the current active span).
   */
  finishSpan(span?: TraceSpan): void {
    const s = span ?? this.activeSpan;
    if (!s || s.endTime) return;

    s.endTime = Date.now();
    if (s.status === 'unset') {
      s.status = 'ok';
    }

    // Emit the span so it can be exported to Tempo
    this.onSpanEnd(s);

    // Restore parent as active (if any)
    if (this.activeSpan?.spanId === s.spanId) {
      this.activeSpan = null; // simplified – real OTel pops from a stack
    }
  }

  /**
   * Mark the current span as errored.
   */
  setError(error: Error, span?: TraceSpan): void {
    const s = span ?? this.activeSpan;
    if (!s) return;

    s.status = 'error';
    s.events.push({
      name: 'exception',
      timestamp: Date.now(),
      attributes: {
        'exception.message': error.message,
        'exception.stacktrace': error.stack ?? '',
      },
    });
  }

  /**
   * Add an attribute to the current span.
   */
  setAttribute(key: string, value: string | number | boolean, span?: TraceSpan): void {
    const s = span ?? this.activeSpan;
    if (!s) return;
    s.attributes[key] = value;
  }

  /**
   * Add a timestamped event to the current span.
   */
  addEvent(name: string, attributes?: Record<string, string | number | boolean>, span?: TraceSpan): void {
    const s = span ?? this.activeSpan;
    if (!s) return;
    s.events.push({ name, timestamp: Date.now(), attributes });
  }

  /**
   * Build the W3C `traceparent` header value for the current span.
   *
   * Format: 00-{traceId}-{spanId}-{flags}
   * Example: 00-0af7651916cd43dd8448eb211c80319c-b7ad6b7169203331-01
   */
  getTraceParent(span?: TraceSpan): string | null {
    const s = span ?? this.activeSpan;
    if (!s) return null;
    return `00-${s.traceId}-${s.spanId}-${this.TRACE_FLAGS_SAMPLED}`;
  }

  /**
   * Build the W3C `tracestate` header value (vendor-specific trace data).
   * For now we include a simple vendor key so Tempo can enrich it.
   */
  getTraceState(span?: TraceSpan): string | null {
    const s = span ?? this.activeSpan;
    if (!s) return null;
    return `dhe=${s.spanId}`;
  }

  /**
   * Return the current trace + span IDs for log correlation.
   */
  getTraceContext(): { traceId: string; spanId: string } | null {
    if (!this.activeSpan) return null;
    return { traceId: this.activeSpan.traceId, spanId: this.activeSpan.spanId };
  }

  /**
   * Get the current trace ID (for Loki log correlation).
   */
  getTraceId(): string | null {
    return this.activeSpan?.traceId ?? null;
  }

  // ---- Span lifecycle callback (overridden by OpenTelemetryService) -----

  /** Registered listeners receive every finished span for export. */
  private spanListeners: Array<(span: TraceSpan) => void> = [];

  onSpanFinished(callback: (span: TraceSpan) => void): void {
    this.spanListeners.push(callback);
  }

  private onSpanEnd(span: TraceSpan): void {
    for (const listener of this.spanListeners) {
      try {
        listener(span);
      } catch {
        // Never let span export break the app
      }
    }
  }

  // ---- ID generation (W3C-compliant) ------------------------------------

  /** Generate a 32-char hex trace ID */
  generateTraceId(): string {
    return this.randomHex(32);
  }

  /** Generate a 16-char hex span ID */
  generateSpanId(): string {
    return this.randomHex(16);
  }

  private randomHex(len: number): string {
    const arr = new Uint8Array(len / 2);
    crypto.getRandomValues(arr);
    return Array.from(arr, (b) => b.toString(16).padStart(2, '0')).join('');
  }

  // ---- No-op fallback ---------------------------------------------------

  private noopSpan(): TraceSpan {
    return {
      traceId: '',
      spanId: '',
      parentSpanId: '',
      name: '',
      kind: 'internal',
      startTime: 0,
      status: 'unset',
      attributes: {},
      events: [],
    };
  }
}
